import { useMemo, useState } from 'react';
import {
  COL_PRICING,
  findColGrid,
  getColUnitPrice,
  type ColMaterial,
  type ColOrigin,
  type ColDeliveryWeeks,
} from '../utils/pricing';
import './PriceSimulator.css';

interface Props {
  /** Bande reflechissante active sur la creation en cours. */
  reflective: boolean;
  onClose: () => void;
}

const ORIGIN_LABELS: Record<ColOrigin, string> = {
  asie: 'Asie',
  europe: 'Europe',
};

const MATERIAL_LABELS: Record<ColMaterial, string> = {
  polyester: '100 % polyester',
  rpet: 'Polyester recyclé RPET',
};

export default function ColPriceSimulator({ reflective, onClose }: Props) {
  const [origin, setOrigin] = useState<ColOrigin>('asie');
  const [weeks, setWeeks] = useState<ColDeliveryWeeks>(5);
  const [material, setMaterial] = useState<ColMaterial>('polyester');
  const [withBand, setWithBand] = useState<boolean>(reflective);
  const [qty, setQty] = useState<number>(300);
  const [raw, setRaw] = useState<string>('300');

  // Options reellement proposees pour l'origine choisie : l'Europe n'a ni RPET
  // ni bande reflechissante, et les delais different d'une origine a l'autre.
  const available = useMemo(() => {
    const grids = COL_PRICING.filter(g => g.origin === origin);
    return {
      weeks: [...new Set(grids.map(g => g.weeks))].sort((a, b) => a - b),
      materials: [...new Set(grids.map(g => g.material))],
      reflectiveAvailable: grids.some(g => g.reflective),
    };
  }, [origin]);

  // Bascule d'origine : on retombe sur des options valides plutot que de
  // laisser une combinaison inexistante a l'ecran.
  const handleOrigin = (next: ColOrigin) => {
    setOrigin(next);
    const grids = COL_PRICING.filter(g => g.origin === next);
    if (!grids.some(g => g.weeks === weeks)) {
      setWeeks(grids[0].weeks);
    }
    if (!grids.some(g => g.material === material)) {
      setMaterial('polyester');
    }
    if (withBand && !grids.some(g => g.reflective)) {
      setWithBand(false);
    }
  };

  const grid = findColGrid({ origin, weeks, material, reflective: withBand });
  const result = getColUnitPrice({ origin, weeks, material, reflective: withBand, qty });
  const total = result.ok ? result.unitPrice * qty : null;

  const handleQty = (val: string) => {
    setRaw(val);
    const n = parseInt(val, 10);
    if (!isNaN(n) && n > 0) setQty(n);
  };

  return (
    <div className="ps-overlay" role="dialog" aria-modal="true" aria-label="Simulateur de tarif">
      <div className="ps-backdrop" onClick={onClose} />
      <div className="ps-card">

        <div className="ps-header">
          <div>
            <h2 className="ps-title">Simulateur de tarif — Tour de cou</h2>
            <p className="ps-subtitle">
              Sublimation quadrichromie incluse
              {grid && <> — Réf.&nbsp;<strong>{grid.ref}</strong></>}
            </p>
            <div className="ps-gender-toggle">
              {(Object.keys(ORIGIN_LABELS) as ColOrigin[]).map(key => (
                <button
                  key={key}
                  type="button"
                  className={`ps-gender-btn${origin === key ? ' is-active' : ''}`}
                  onClick={() => handleOrigin(key)}
                >
                  {ORIGIN_LABELS[key]}
                </button>
              ))}
            </div>
          </div>
          <button className="ps-close" type="button" onClick={onClose} aria-label="Fermer">×</button>
        </div>

        <div className="ps-body">
          <div className="ps-controls">
            <div>
              <label className="ps-label">Délai</label>
              <div className="ps-delivery-tabs">
                {available.weeks.map(w => (
                  <button
                    key={w}
                    type="button"
                    className={`ps-delivery-tab ${weeks === w ? 'is-active' : ''}`}
                    onClick={() => setWeeks(w)}
                  >
                    {w} semaines
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="ps-label">Matière</label>
              <div className="ps-delivery-tabs">
                {available.materials.map(m => (
                  <button
                    key={m}
                    type="button"
                    className={`ps-delivery-tab ${material === m ? 'is-active' : ''}`}
                    onClick={() => setMaterial(m)}
                  >
                    {MATERIAL_LABELS[m]}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="ps-label">Option</label>
              <label className="ps-option-row">
                <input
                  type="checkbox"
                  checked={withBand}
                  disabled={!available.reflectiveAvailable}
                  onChange={e => setWithBand(e.target.checked)}
                />
                <span>
                  Bande réfléchissante
                  {!available.reflectiveAvailable && (
                    <em className="ps-option-note"> — non proposée en Europe</em>
                  )}
                </span>
              </label>
            </div>

            <div className="ps-qty-row">
              <label className="ps-label" htmlFor="ps-qty">Quantité</label>
              <div className="ps-qty-input-wrap">
                <input
                  id="ps-qty"
                  type="number"
                  min={1}
                  className="ps-qty-input"
                  value={raw}
                  onChange={e => handleQty(e.target.value)}
                  onBlur={() => setRaw(String(qty))}
                />
                <span className="ps-qty-unit">unités</span>
              </div>
              {!result.ok && result.reason === 'quantiteInsuffisante' && (
                <p className="ps-qty-warn">Min.&nbsp;{result.minQty}&nbsp;u.</p>
              )}
            </div>
          </div>

          <div className="ps-right">
            <div className="ps-table-wrap">
              <table className="ps-table">
                <thead>
                  <tr>
                    <th>Quantité</th>
                    <th>Prix unit. HT</th>
                    <th>Total HT</th>
                  </tr>
                </thead>
                <tbody>
                  {(grid?.tiers ?? []).map((tier, i, tiers) => {
                    const isActive =
                      qty >= tier.minQty &&
                      (i === tiers.length - 1 || qty < tiers[i + 1].minQty);
                    return (
                      <tr key={tier.minQty} className={isActive ? 'is-active' : ''}>
                        <td>{tier.minQty.toLocaleString('fr-FR')}&nbsp;+</td>
                        <td className="ps-price">{tier.price.toFixed(2)}&nbsp;€</td>
                        <td className="ps-total">
                          {isActive && total != null
                            ? total.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
                            : '≥ ' + (tier.price * tier.minQty).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="ps-result">
              {result.ok && total != null ? (
                <>
                  <div className="ps-result-row">
                    <span className="ps-result-label">Prix unitaire HT</span>
                    <span className="ps-result-value">{result.unitPrice.toFixed(2)}&nbsp;€</span>
                  </div>
                  <div className="ps-result-row ps-result-row--total">
                    <span className="ps-result-label">Total HT ({qty.toLocaleString('fr-FR')} unités)</span>
                    <span className="ps-result-value ps-result-value--total">
                      {total.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}&nbsp;€
                    </span>
                  </div>
                </>
              ) : (
                <p className="ps-result-na">
                  {!result.ok && result.reason === 'quantiteInsuffisante'
                    ? `Min. ${result.minQty} unités pour cette combinaison`
                    : 'Combinaison non proposée au tarif'}
                </p>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
